import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { TranslateModule } from '@ngx-translate/core';

import {
	AlarisBalanceService,
	AlarisButtonComponent,
	AlarisCostComponent,
	AlarisDropdownActionsComponent,
	AlarisDropdownComponent,
	AlarisDropdownDirective,
	AlarisEditPanelService,
	AlarisIconDirective,
	AlarisLinkComponent,
	AlarisPBACDirective,
	AlarisProfileService,
	AlarisProgressLineComponent,
	AlarisTabsButtonComponent,
	AlarisTagComponent,
	AlarisTooltipDirective,
	ChannelUtilsService,
	DateFormatPipe,
	EditPanelInputData,
	formatBytes,
	RowActionSimple,
} from '@campaign-portal/components-library';
import { TabButtonEntity } from '@campaign-portal/components-library/lib/tabs-button/src/base';

import { Campaign, CampaignStatus } from '@campaign-portal/namespace/entities/campaigns/specs';
import { exist } from '@campaign-portal/namespace/common/id';
import {
	PreModerationDeclineType,
	PreModerationEventStatus,
	TrafficType,
} from '@campaign-portal/namespace/common/enums';

import { CP_PERMISSIONS } from '@helpers/types/permissions';
import { CampaignsService } from '../../services/campaigns.service';
import { CampaignUtilsService } from '../../services/campaign-utils.service';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { ContactGroupPipe } from '@helpers/repo/repo.pipe';
import { PhoneMessageComponent } from '../../shared/phone-message/phone-message.component';
import { UrlShortenerUtilsService } from '../../../url-shortener/url-shortener-utils.service';
import { CampaignPlainContactsComponent } from './campaign-plain-contacts/campaign-plain-contacts.component';
import { CampaignDetailsChartComponent } from './campaign-details-chart/campaign-details-chart.component';
import { CampaignCascadeTableComponent } from './campaign-cascade-table/campaign-cascade-table.component';
import { CampaignDetailsActionsService } from './campaign-details.actions.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageTemplatesService } from '../../services/message-templates.service';

@Component({
	selector: 'app-campaign-details',
	templateUrl: './campaign-details.component.html',
	styleUrls: ['./campaign-details.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [CampaignDetailsActionsService],
	imports: [
		TranslateModule,
		AlarisPBACDirective,
		AlarisButtonComponent,
		AlarisIconDirective,
		AlarisDropdownComponent,
		AlarisDropdownActionsComponent,
		NgTemplateOutlet,
		AlarisTagComponent,
		AlarisProgressLineComponent,
		ContactGroupPipe,
		AlarisDropdownDirective,
		AlarisTooltipDirective,
		AlarisTabsButtonComponent,
		PhoneMessageComponent,
		DecimalPipe,
		AlarisLinkComponent,
		CampaignPlainContactsComponent,
		CampaignDetailsChartComponent,
		AlarisCostComponent,
		CampaignCascadeTableComponent,
		DateFormatPipe,
	],
	standalone: true,
})
export class CampaignDetailsComponent {
	campaign: Campaign<exist>;
	readonly TrafficType = TrafficType;
	readonly CampaignStatus = CampaignStatus;
	readonly CP_PERMISSIONS = CP_PERMISSIONS;
	readonly PreModerationDeclineType = PreModerationDeclineType;
	isOpened = false;

	readonly hasShortUrlSection: boolean;
	readonly hasPreModeratedCampaignChannels: boolean;
	readonly position: ConnectedPosition[] = [
		{
			originX: 'end',
			originY: 'bottom',
			overlayX: 'end',
			overlayY: 'top',
			offsetY: 8,
		},
	];

	// message template
	tabs: TabButtonEntity<TrafficType>[] = [];
	readonly tabsControl = new FormControl<TrafficType>(TrafficType.SMS, { nonNullable: true });
	readonly actions: RowActionSimple<Campaign<exist>>[] = [
		this.actionsService.start,
		this.actionsService.resume,
		this.actionsService.pause,
		this.actionsService.cancel,
		this.actionsService.repeat,
		this.actionsService.delete,
	];
	protected readonly formatBytes = formatBytes;
	private readonly destroyRef = inject(DestroyRef);

	constructor(
		public readonly bs: AlarisBalanceService,
		public readonly cu: ChannelUtilsService,
		public readonly campaignUtils: CampaignUtilsService,
		public readonly shortenerUtils: UrlShortenerUtilsService,
		@Inject(EditPanelInputData) private readonly inputData: EditPanelInputData,
		private readonly actionsService: CampaignDetailsActionsService,
		private readonly profile: AlarisProfileService,
		private readonly editPanel: AlarisEditPanelService,
		private readonly campaignsService: CampaignsService,
		private readonly templatesService: MessageTemplatesService
	) {
		this.campaign = this.inputData.campaign as Campaign<exist>;
		this.initializeTabs();
		this.hasShortUrlSection = this.checkShortUrlSection();
		this.hasPreModeratedCampaignChannels = this.checkPreModeratedCampaignChannels();
	}

	export(id: string, fileName?: string): void {
		this.campaignsService.export(id, fileName).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	action(campaign: Campaign<exist>): void {
		switch (this.campaign.info.status) {
			case CampaignStatus.SCHEDULED:
				this.actionsService.start.action(campaign);
				break;
			case CampaignStatus.PAUSED:
				this.actionsService.resume.action(campaign);
				break;
			case CampaignStatus.IN_PROGRESS:
				this.actionsService.pause.action(campaign);
				break;
			case CampaignStatus.WAITING_FOR_APPROVAL:
				this.actionsService.cancel.action(campaign);
				break;
			case CampaignStatus.FAILED:
			case CampaignStatus.CANCELLED:
			case CampaignStatus.COMPLETED:
				if (Object.hasOwnProperty.call(campaign.setup, 'file')) {
					this.actionsService.delete.action(campaign);
				} else {
					this.actionsService.repeat.action(campaign);
				}
				break;
			default:
				return;
		}
	}

	navigateToShortener(): void {
		const allowed = this.profile.allowed([CP_PERMISSIONS.URL_SHORTENER_E]);
		const shortLink = this.campaign.info.shortLink;
		if (shortLink && allowed) {
			this.shortenerUtils.navigateToShortener(shortLink);
			this.editPanel.close();
		}
	}

	private initializeTabs(): void {
		this.tabs = this.campaign.setup.channels.map((settings) => {
			return {
				label: this.cu.name(settings.trafficType),
				value: settings.trafficType,
			};
		});
		this.tabsControl.patchValue((this.tabs[0]?.value as TrafficType) ?? TrafficType.SMS);
	}

	private checkPreModeratedCampaignChannels(): boolean {
		return this.campaign.setup.channels.some((settings) => {
			const isPreModerated = this.campaignUtils.isPreModeratedChannel(settings.trafficType);
			const templateId = settings.message?.templateID;
			const isConfirmedTemplateUsed = Boolean(
				templateId && this.templatesService.map.get(templateId)?.status === PreModerationEventStatus.CONFIRMED
			);
			return isPreModerated && !isConfirmedTemplateUsed;
		});
	}

	private checkShortUrlSection(): boolean {
		const hasShortUrl = (this.campaign.setup?.channels ?? []).some((settings) => {
			const message = this.campaignUtils.message(this.campaign, settings.trafficType);
			return this.shortenerUtils.extractShortLink(message);
		});

		return Boolean(
			this.campaign.info.shortLink ||
				(this.profile.allowed([CP_PERMISSIONS.URL_SHORTENER_E]) &&
					this.campaign.info.status === CampaignStatus.SCHEDULED &&
					hasShortUrl)
		);
	}
}
